"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import { useAuth } from "@/components/auth/AuthProvider";

/**
 * Wraps a free tool's interactive island so using it requires a free account.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A CLIENT-SIDE GATE AND NOT A SERVER REDIRECT OR MIDDLEWARE
 *
 * Every `/free/**` page is a server component with no dynamic server read
 * (no `cookies()`/`headers()`), which is what lets `npm run build` prerender
 * it as static HTML and rank it in search — see `FreeToolShell.tsx`'s own
 * header comment. A server-side redirect or a middleware check would force
 * that page dynamic (`ƒ` instead of `○`/`●`), which is exactly the property
 * these pages exist to keep. So the account requirement lives here instead:
 * the page still prerenders in full — headline, article, JSON-LD, footer —
 * and only the interactive tool decides at runtime, from `AuthProvider`'s
 * client context, whether to render itself or this gate.
 *
 * WHY `loading` GETS ITS OWN BRANCH
 *
 * `AuthProvider` resolves the session after hydration, so for one render pass
 * every visitor — signed in or not — looks signed out. Skipping straight to
 * "no user, show the gate" would flash the gate at every subscriber on every
 * load before quietly swapping to the tool a moment later. The placeholder
 * below is rendered instead, sized like the gate card, so neither the gate nor
 * the tool appears until the real answer is known.
 *
 * WHY ANY ACCOUNT IS ENOUGH
 *
 * The owner's rule is "free tools need a free account", not a paid plan — this
 * checks `user` only, never a feature or plan entitlement. That is also why
 * this is not `guardTool`/`_guard.ts`: those resolve a `FeatureKey` against a
 * plan, and a free tool has neither.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Height shared by the loading placeholder and the gate card, so settling into either is not a jump. */
const GATE_MIN_HEIGHT = "min-h-[22rem]";

export function FreeToolGate({
  path,
  children,
}: {
  /** This tool's own route, e.g. `/free/joaat` — carried through as `next` so sign-up/login return here. */
  path: string;
  children: ReactNode;
}) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        aria-hidden="true"
        data-testid="free-tool-gate-loading"
        className={`animate-pulse rounded-2xl border border-border/60 bg-muted/30 ${GATE_MIN_HEIGHT}`}
      />
    );
  }

  if (!user) {
    return <FreeToolGateCard path={path} />;
  }

  return <>{children}</>;
}

function FreeToolGateCard({ path }: { path: string }) {
  const next = encodeURIComponent(path);
  return (
    <div
      data-testid="free-tool-gate"
      className={`flex flex-col items-center justify-center gap-4 rounded-2xl border border-border/60 bg-muted/20 p-8 text-center sm:p-12 ${GATE_MIN_HEIGHT}`}
    >
      <div className="max-w-sm space-y-2">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          Free tools need a free account
        </h2>
        <p className="text-sm leading-6 text-muted-foreground">
          No card, no download — the tool runs in your browser once you&apos;re signed in.
        </p>
      </div>
      <div className="flex flex-col items-center gap-3 sm:flex-row">
        <Link
          href={`/auth?mode=sign-up&next=${next}`}
          className="inline-flex items-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
        >
          Create free account
        </Link>
        <Link
          href={`/auth?next=${next}`}
          className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          Log in
        </Link>
      </div>
    </div>
  );
}
